import { Plus, X } from "lucide-react";
import { SearchSelect } from "../SearchSelect";
import { SOURCES, SOURCE_KEY, sourceLabel } from "./contactsKit";

/**
 * Rule conditions (design: components/crm/rules.tsx) on the backend's rule
 * format. Scoring rules (lead_scoring_rules.conditions) and assignment rules
 * (auto_assignment_rules.conditions.rules) are both lists of
 * { field, operator, value } that must all hold (item 164); the backend judges
 * them with one evaluator, services/leadRuleConditions.js.
 *
 * Every design field can be saved (item 165). Source, Interested service,
 * Budget and Campaign (UTM) are lead columns; City, Tag, Message contains,
 * Replied on WhatsApp, Appointment booked and Days since last reply are facts
 * the backend looks up (the contact, the lead's tags, the WhatsApp inbox,
 * appointments) when a lead comes in.
 *
 * The live tests on the rules screens run on GET /api/leads rows, which carry
 * the lead's columns, contact_city and tags but not its WhatsApp messages or
 * appointments. `conditionVerdict()` therefore answers true, false, or null
 * ("can't tell here") — never a guess. The design's sample PEOPLE list is not
 * exported: people come from GET /api/staff/assignable-users.
 */

export const RULE_FIELDS = ["Source", "Interested service", "Budget", "City", "Campaign (UTM)", "Tag", "Message contains", "Replied on WhatsApp", "Appointment booked", "Days since last reply"];
export const defaultCondition = { field: "Source", operator: "is", value: SOURCES[0] };

/** Design field → backend field key. Other lead columns a rule made elsewhere may use get a readable name. */
const FIELD_KEY = {
  Source: "source", "Interested service": "interested_service", Budget: "budget_range", "Campaign (UTM)": "utm_campaign",
  City: "city", Tag: "tag", "Message contains": "message_contains", "Replied on WhatsApp": "replied_whatsapp",
  "Appointment booked": "appointment_booked", "Days since last reply": "days_since_reply",
};
/** Fields that aren't lead columns: the backend looks them up (services/leadRuleConditions.js EXTENDED_FIELDS). */
const DERIVED = ["city", "tag", "message_contains", "replied_whatsapp", "appointment_booked", "days_since_reply"];
/** Fields the panel can't see on a lead row: they need the WhatsApp inbox or appointments. */
const SERVER_ONLY = ["message_contains", "replied_whatsapp", "days_since_reply"];
const OTHER_FIELDS = { priority: "Priority", status: "Status", email: "Email", phone: "Phone", first_name: "First name", last_name: "Last name", lead_score: "Lead score", utm_source: "UTM source", utm_medium: "UTM medium" };
const FIELD_LABEL = { ...Object.fromEntries(Object.entries(FIELD_KEY).map(([k, v]) => [v, k])), ...OTHER_FIELDS };

const OPERATOR_KEY = { is: "equals", "is not": "not_equals", contains: "contains", "does not contain": "not_contains", "starts with": "starts_with", "is more than": "greater_than", "is less than": "less_than", "is empty": "is_empty", "is not empty": "is_not_empty" };
const OPERATOR_LABEL = Object.fromEntries(Object.entries(OPERATOR_KEY).map(([k, v]) => [v, k]));
const NO_VALUE = ["is empty", "is not empty"];
const BACKEND_OPERATORS = ["equals", "not_equals", "contains", "not_contains", "starts_with", "greater_than", "less_than", "between", "is_empty", "is_not_empty"];

const staticOptions = { Source: SOURCES, "Replied on WhatsApp": ["Yes", "No"], "Appointment booked": ["Yes", "No"] };
const operatorsFor = (field) => (
  field === "Budget" ? ["is more than", "is less than", "is", "is not"]
    : field === "Days since last reply" ? ["is more than", "is less than", "is", "is empty"]
      : field === "Message contains" ? ["contains", "does not contain"]
        : field === "Campaign (UTM)" || field === "Interested service" || field === "City" ? ["contains", "is", "is not"]
          : field === "Tag" ? ["is", "is not", "is empty", "is not empty"]
            : ["is", "is not"]);

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,49}$/;
/** Why a condition can't be saved, or "" when it can. */
export const unsupportedField = (condition) => (condition && !toBackendCondition(condition) ? `Pick a field for this condition.` : "");

/** Design condition → backend { field, operator, value } (null when the field can't be named for the backend). */
export function toBackendCondition(condition) {
  if (!condition) return null;
  // A rule saved elsewhere may test a lead column this builder doesn't list; it comes back as its key and is kept.
  const field = FIELD_KEY[condition.field] || Object.keys(OTHER_FIELDS).find((k) => OTHER_FIELDS[k] === condition.field) || (KEY_PATTERN.test(condition.field || "") ? condition.field : null);
  if (!field) return null;
  const value = field === "source" ? SOURCE_KEY[condition.value] || condition.value : condition.value;
  // An operator only another client offers (e.g. "between") is kept as is, with its second value.
  const operator = OPERATOR_KEY[condition.operator] || (BACKEND_OPERATORS.includes(condition.operator) ? condition.operator : "equals");
  return { field, operator, value: NO_VALUE.includes(condition.operator) ? null : value, ...(condition.value2 !== undefined && condition.value2 !== null ? { value2: condition.value2 } : {}) };
}
/** Backend { field, operator, value } (or { field_name, field_value }) → design condition. */
export function fromBackendCondition(c) {
  const field = c.field ?? c.field_name;
  const operator = c.operator;
  const value = c.value !== undefined ? c.value : c.field_value;
  return {
    field: FIELD_LABEL[field] || field,
    operator: OPERATOR_LABEL[operator] || operator,
    value: field === "source" ? sourceLabel(value) : value ?? "",
    ...(c.value2 !== undefined && c.value2 !== null ? { value2: c.value2 } : {}),
  };
}

/** One condition of a rule. `options` adds value lists for fields that come from the clinic (e.g. { Tag: [...] }). */
export function ConditionBuilder({ value, onChange, options: extra = {} }) {
  const fieldOptions = { ...staticOptions, ...extra };
  const options = fieldOptions[value.field];
  const fields = RULE_FIELDS.includes(value.field) ? RULE_FIELDS : [...RULE_FIELDS, value.field];
  const operators = operatorsFor(value.field).includes(value.operator) ? operatorsFor(value.field) : [...operatorsFor(value.field), value.operator];
  const updateField = (field) => onChange({ field, operator: operatorsFor(field)[0], value: fieldOptions[field]?.[0] ?? "" });
  const valueOptions = options && value.value && !options.includes(value.value) ? [...options, value.value] : options;
  return <div className="flex flex-wrap items-center gap-2 text-small">
    <span className="text-muted">When</span>
    <SearchSelect size="sm" ariaLabel="Condition field" value={value.field} options={fields} onChange={updateField} />
    <select aria-label="Condition operator" value={value.operator} onChange={(e) => onChange({ ...value, operator: e.target.value })} className="h-9 rounded-full border border-line bg-paper px-3 text-ink outline-none focus:border-ink">
      {operators.map((op) => <option key={op}>{op}</option>)}
    </select>
    {NO_VALUE.includes(value.operator) ? null : valueOptions ? valueOptions.length > 5 ? <SearchSelect size="sm" ariaLabel="Condition value" value={value.value} options={valueOptions} onChange={(next) => onChange({ ...value, value: next })} /> : <select aria-label="Condition value" value={value.value} onChange={(e) => onChange({ ...value, value: e.target.value })} className="h-9 max-w-full rounded-full border border-line bg-mist px-3 font-semibold text-ink outline-none focus:border-ink">{valueOptions.map((option) => <option key={option}>{option}</option>)}</select> : <input aria-label="Condition value" value={value.value} onChange={(e) => onChange({ ...value, value: e.target.value })} placeholder={value.field === "Budget" ? "50000" : "Type a value"} inputMode={value.field === "Budget" || value.field === "Days since last reply" ? "numeric" : "text"} className="h-9 w-40 rounded-full border border-line bg-mist px-3 font-semibold text-ink outline-none focus:border-ink" />}
  </div>;
}

export function conditionSentence(condition) {
  if (!condition) return "Everything else";
  if (condition.field === "Message contains") return `When a WhatsApp message ${["does not contain", "is not"].includes(condition.operator) ? "doesn't contain" : "contains"} ${condition.value}`;
  if (condition.field === "Days since last reply" && condition.operator === "is empty") return "When they have never replied";
  if (condition.field === "Tag" && NO_VALUE.includes(condition.operator)) return condition.operator === "is empty" ? "When the lead has no tags" : "When the lead has a tag";
  const noun = condition.field === "Campaign (UTM)" ? "Campaign" : condition.field;
  if (condition.operator === "between") return `When ${noun} is between ${condition.value} and ${condition.value2}`;
  if (NO_VALUE.includes(condition.operator)) return `When ${noun} ${condition.operator}`;
  const value = condition.field === "Budget" && condition.operator === "is more than" ? `₹${Number(condition.value || 0).toLocaleString("en-IN")}` : condition.value;
  return `When ${noun} ${condition.operator} ${value}`;
}

/** One condition plus the "+ And" conditions after it (item 164). `max` is the backend's limit of 20. */
export function ConditionList({ value, onChange, options, max = 20 }) {
  const update = (i, next) => onChange(value.map((c, j) => (j === i ? next : c)));
  return <div className="flex flex-col gap-3">
    {value.map((condition, i) => <div key={i} className="flex items-start gap-2">
      {i > 0 && <span className="mt-2 w-9 shrink-0 text-caption font-semibold text-muted">And</span>}
      <div className="min-w-0 flex-1"><ConditionBuilder value={condition} onChange={(next) => update(i, next)} options={options} /></div>
      {value.length > 1 && <button type="button" aria-label="Remove condition" onClick={() => onChange(value.filter((_, j) => j !== i))} className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-danger"><X size={14} /></button>}
    </div>)}
    {value.length < max && <button type="button" onClick={() => onChange([...value, { ...defaultCondition }])} className="inline-flex items-center gap-1 self-start rounded-full px-3 py-1.5 text-caption font-semibold text-sky-deep hover:bg-sky-soft"><Plus size={13} />And</button>}
  </div>;
}

/* ---------- Matching (mirrors services/leadRuleConditions.js) ---------- */

const truthy = (v) => ["yes", "true", "1", "y"].includes(String(v ?? "").trim().toLowerCase());

/** compareScalar: one value against a condition, case-insensitive; NaN never passes a number test. */
function compareScalar(raw, { operator, value, value2 }) {
  const fieldValue = raw === null || raw === undefined ? undefined : String(raw).toLowerCase();
  const condValue = value === null || value === undefined ? undefined : String(value).toLowerCase();
  const a = parseFloat(raw);
  const b = parseFloat(value);
  const c = parseFloat(value2);
  switch (operator) {
    case "equals": return fieldValue === condValue;
    case "not_equals": return fieldValue !== condValue;
    case "contains": return !!fieldValue && fieldValue.includes(condValue ?? "");
    case "not_contains": return !(fieldValue || "").includes(condValue ?? "");
    case "starts_with": return !!fieldValue && fieldValue.startsWith(condValue ?? "");
    case "greater_than": return !Number.isNaN(a) && !Number.isNaN(b) && a > b;
    case "less_than": return !Number.isNaN(a) && !Number.isNaN(b) && a < b;
    case "between": return !Number.isNaN(a) && !Number.isNaN(b) && !Number.isNaN(c) && a >= b && a <= c;
    case "is_empty": return !fieldValue || fieldValue.trim() === "";
    case "is_not_empty": return !!fieldValue && fieldValue.trim() !== "";
    default: return false;
  }
}
/** compareList: tags — "is X" means one of them is X, "is not X" means none is. */
function compareList(list, cond) {
  const items = (list || []).map(String);
  switch (cond.operator) {
    case "is_empty": return items.length === 0;
    case "is_not_empty": return items.length > 0;
    case "not_equals": return !items.some((t) => compareScalar(t, { ...cond, operator: "equals" }));
    case "not_contains": return !items.some((t) => compareScalar(t, { ...cond, operator: "contains" }));
    default: return items.some((t) => compareScalar(t, cond));
  }
}
function compareBoolean(actual, cond) {
  switch (cond.operator) {
    case "equals": return actual === truthy(cond.value);
    case "not_equals": return actual !== truthy(cond.value);
    case "is_empty": return !actual;
    case "is_not_empty": return actual;
    default: return false;
  }
}

/**
 * Does a lead row (GET /api/leads) meet a backend condition? true / false, or
 * null when the answer needs data the row doesn't carry (WhatsApp replies,
 * messages, an appointment for a lead not at a "booked" stage).
 */
export function conditionVerdict(condition, lead) {
  const field = condition.field ?? condition.field_name;
  const cond = { operator: condition.operator, value: condition.value !== undefined ? condition.value : condition.field_value, value2: condition.value2 ?? null };
  if (!lead) return null;
  if (SERVER_ONLY.includes(field)) return null;
  if (field === "city") return compareScalar(lead.city || lead.contact_city || null, cond);
  if (field === "tag") return Array.isArray(lead.tags) ? compareList(lead.tags.map((t) => (t && typeof t === "object" ? t.name : t)).filter(Boolean), cond) : null;
  if (field === "appointment_booked") {
    // A lead at a stage mapped to "booked" has one; otherwise only the server can check appointments.
    if (lead.status === "booked") return compareBoolean(true, cond);
    return null;
  }
  return compareScalar(lead[field], cond);
}

/** All conditions hold (AND): false if any fails, null if none fails but some can't be told here. Empty holds. */
export function conditionsVerdict(conditions, lead) {
  let unknown = false;
  for (const c of conditions || []) {
    const v = conditionVerdict(c, lead);
    if (v === false) return false;
    if (v === null) unknown = true;
  }
  return unknown ? null : true;
}

/** The design names of the fields in `conditions` the panel can't check on a lead row. */
export function uncheckedFields(conditions, lead) {
  return [...new Set((conditions || []).filter((c) => conditionVerdict(c, lead) === null).map((c) => FIELD_LABEL[c.field ?? c.field_name] || c.field))];
}

/** The GET /api/leads `filters` entry that counts the leads a backend condition matches, or null when it can't be expressed. */
export function leadFilterFor(condition) {
  const field = condition.field ?? condition.field_name;
  // Derived fields aren't lead columns, so the list endpoint can't filter on them.
  if (DERIVED.includes(field)) return null;
  const enumField = ["source", "status", "priority"].includes(field);
  const op = { equals: "eq", not_equals: "neq", contains: enumField ? null : "contains", is_empty: "is_empty", is_not_empty: enumField ? null : "not_empty" }[condition.operator];
  if (!op) return null;
  return { column: field, operator: op, value: condition.value !== undefined ? condition.value : condition.field_value };
}
/** Filters for every condition of a rule, or null when any can't be expressed. */
export function leadFiltersFor(conditions) {
  const list = (conditions || []).map(leadFilterFor);
  return list.every(Boolean) ? list : null;
}
